import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfBrush,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { C } from '../../core/c';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/transforms';
import '../../pdf/draw/label';

/**
 * Losovani (Lottery Wheel)
 *
 * Layout: 3 pages with different grid sizes (4x5, 6x8, 10x12).
 * Each page has a spinning wheel in the center with question slices,
 * surrounded by answer cards on the border.
 *
 * Migrated from C# EduFrame.Single.Generators.Losovani
 */
export class Losovani implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 3;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const configs: { x: number; y: number }[] = [
      { x: 4, y: 5 },
      { x: 6, y: 8 },
      { x: 10, y: 12 },
    ];

    for (let pageIdx = 0; pageIdx < configs.length; pageIdx++) {
      const page = pdf.addPage();
      const config = configs[pageIdx];
      this.generatePage(page, opts, config.x, config.y);
    }
  }

  private generatePage(page: any, opts: OptsLabel, countX: number, countY: number): void {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

    const items = opts.list;

    const borderPositions: C[] = [];

    for (let x = 1; x < countX; x++) borderPositions.push(new C(x, 0));
    for (let y = 1; y < countY; y++) borderPositions.push(new C(countX - 1, y));
    for (let x = countX - 2; x >= 0; x--) borderPositions.push(new C(x, countY - 1));
    for (let y = countY - 2; y >= 0; y--) borderPositions.push(new C(0, y));

    const diameter = Math.min(size.width * (countX - 2), size.height * (countY - 2));
    const ellipseRect = new PdfRect(
      PdfSize.A4.width / 2 - diameter / 2,
      PdfSize.A4.height / 2 - diameter / 2,
      diameter,
      diameter
    );

    const answers = new Map<string, string>();
    const shuffledPositions = [...borderPositions].sort(() => opts.data.random.next(0, 2) - 0.5);

    for (let i = 0; i < items.length && i < shuffledPositions.length; i++) {
      const pos = shuffledPositions[i];
      const key = pos.toHash();
      answers.set(key, items[i].right.getText() ?? '');
    }

    for (const pos of borderPositions) {
      const key = pos.toHash();
      if (!answers.has(key)) {
        const randomItem = items[opts.data.random.next(0, items.length)];
        answers.set(key, randomItem.right.getText() ?? '');
      }
    }

    const allAnswerTexts = items.map((item) => item.right.getText() ?? '');
    const answerFont = page.adjustFontMulti(
      allAnswerTexts.map((t) => [t]),
      GUtil.baseFont,
      size
    );

    for (const pos of borderPositions) {
      const key = pos.toHash();
      const answerText = answers.get(key) ?? '';
      const rect = new PdfRect(
        origin.x + pos.x * size.width,
        origin.y + pos.y * size.height,
        size.width,
        size.height
      );

      page.drawRectangle(GUtil.basePen, null, rect);

      const matchingItem = items.find((i) => i.right.getText() === answerText);
      if (matchingItem) {
        page.drawLabel(matchingItem.right, { rect, font: answerFont });
      } else {
        page.drawString(answerText, answerFont, PdfBrush.Black, null, rect, ETextAlign.Center);
      }
    }

    const brushes = [
      new PdfBrush(PdfColor.fromRgb(230, 230, 230)),
      new PdfBrush(PdfColor.fromRgb(200, 200, 200)),
    ];

    const sliceCount = Math.min(items.length, 50);
    const sliceAngle = 360 / sliceCount;

    const angleRad = (Math.PI * 2) / sliceCount;
    const textWidth = ellipseRect.width * 0.45;
    const maxTextHeight = ellipseRect.height * Math.tan(angleRad / 2);
    const textHeight = Math.min(ellipseRect.height * 0.08, maxTextHeight);

    const allQuestionTexts = items.map((item) => item.left.getText() ?? '');
    const questionFont = page.adjustFont(textWidth, allQuestionTexts, GUtil.baseFont);

    for (let i = 0; i < sliceCount; i++) {
      const angle = sliceAngle * i;

      page.save();
      page.rotateAtTransform(angle, new PdfPoint(ellipseRect.centerX, ellipseRect.centerY));

      const startAngle = -sliceAngle / 2;
      page.drawPie(GUtil.basePen, brushes[i % brushes.length], ellipseRect, startAngle, sliceAngle);

      if (i < items.length) {
        const textRect = new PdfRect(
          ellipseRect.right - textWidth * 1.02,
          ellipseRect.centerY - textHeight / 2,
          textWidth,
          textHeight
        );

        page.drawLabel(items[i].left, { rect: textRect, font: questionFont, singleLine: true });
      }

      page.restore();
    }
  }
}
